import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
    EntryPoint,
    FieldType,
    LegType,
    SlideType,
    TabTypeProp,
    TabTypePropDefault,
} from '../../../models';
import Field from './Field';
import { LocationFragment } from '../MobileFragments';
import { LocationAutocompleteSource } from '../../LocationAutocomplete/LocationAutocompleteSource';
import { PreviousSearchesSourceMobile } from '../../PreviousSearches/PreviousSearchesSourceMobile';
import { TopDestinationsSourceMobile } from '../../TopDestinations/TopDestinationsSourceMobile';
import { ConnectedSegmentArrival } from '../Segment/SegmentArrival';
import { ErrorUtils } from '../../../utils/ErrorUtils';
import { useSegmentContext } from '../Segment/SegmentManager/Context';
import { Config, TrackingUtils, withPageContext } from '../../../utils';

export const ArrivalField = ({
    activeProduct,
    isTopDestination,
    getLocationText,
    icon,
    trackEvent,
    ErrorMessageComponent,
}) => {
    const { t } = useTranslation();
    const { segment } = useSegmentContext();
    const { id } = segment;
    const direction = Config.get('isRtl') ? 'rtl' : 'ltr';
    const maxPreviousSearches = (activeProduct === EntryPoint.HOTELS ) ? 0 : 4;
    const placeholder = t(
        `${activeProduct === EntryPoint.HOTELS ? 'home.hsaSearch' : 'citySearch'}.destination`,
    );
    const getErrorMessage = (error) => t(ErrorUtils.getLocationErrorKey(error, activeProduct));
    const getSources = () => {
        const previousSearches = PreviousSearchesSourceMobile({
            maxPreviousSearches,
            field: FieldType.ARRIVAL,
            id,
            activeProduct,
        });
        const locationAutocomplete = LocationAutocompleteSource({
            departureOrArrival: LegType.ARRIVAL,
            activeProduct,
        });
        const topDestinations = TopDestinationsSourceMobile({ id, activeProduct });
        return isTopDestination ? [locationAutocomplete, previousSearches, topDestinations] : [locationAutocomplete, previousSearches];
    };

    useEffect(() => {
        if (activeProduct === EntryPoint.HOTELS || activeProduct === EntryPoint.DP) {
            let count = 0;
            let interval;
            interval = setInterval(() => {
                if (count >= TrackingUtils.MAX_ATTEMPTS) {
                    trackEvent({
                        action: TrackingUtils.ACTION.HOTELS,
                        label: TrackingUtils.LABEL.fieldRendered(activeProduct, 'searchbox')
                    });
                    clearInterval(interval);
                }
                count++;
            }, TrackingUtils.ATTEMPT_TIMEOUT);
        }
    }, [trackEvent, activeProduct]);

    return (
        <ConnectedSegmentArrival segmentId={id}>
            {({ error, setValue, value, isDisabled }) => {
                return (
                    <>
                        <Field
                            errorMessage={getErrorMessage(error)}
                            icon={icon}
                            id={FieldType.ARRIVAL}
                            placeholder={placeholder}
                            value={getLocationText(value, direction)}
                            isDisabled={isDisabled}
                            ErrorMessageComponent={ErrorMessageComponent}
                        />
                        <LocationFragment
                            id={SlideType.ARRIVAL}
                            setValue={setValue}
                            sources={getSources()}
                            value={value}
                            error={error}
                            activeProduct={activeProduct}
                        />
                    </>
                );
            }}
        </ConnectedSegmentArrival>
    );
};

ArrivalField.propTypes = {
    isTopDestination: PropTypes.bool,
    getLocationText: PropTypes.func,
    icon: PropTypes.string,
    activeProduct: TabTypeProp,
    trackEvent: PropTypes.func,
    ErrorMessageComponent: PropTypes.func,
};

ArrivalField.defaultProps = {
    isTopDestination: false,
    activeProduct: TabTypePropDefault,
};

const mapStateToProps = ({ activeProduct }) => ({
    activeProduct: activeProduct.activeProduct,
});

const connected = connect(mapStateToProps, {})(withPageContext(ArrivalField));
export { connected as ConnectedArrivalField };
